import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetIdentity } from 'ra-core';

import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { EVENTO_MENSAGENS, listarConversas, naTela, ouvirMensagens, preparar } from './mensagensApi';
import { mensagemDeErro, ordenarConversas, totalNaoLidas, type Conversa } from './mensagensLogica';

/** Conferida de segurança, caso o tempo real caia. */
const INTERVALO_MS = 60 * 1000;

/**
 * Minhas conversas com o total de não lidas — a fonte do número no menu e da
 * lista da tela. Atualiza com o aviso em tempo real, quando eu mesmo mudo
 * algo (enviei, li), ao voltar para a aba e, por garantia, a cada minuto.
 *
 * Perfis de leitura (migration 184) não usam o chat: o hook nem consulta.
 */
export function useConversas({
  preparar: devePreparar = false,
  ativo = true,
}: { preparar?: boolean; /** false = não consulta nada (Mensagens fora do menu). */ ativo?: boolean } = {}) {
  const { identity } = useGetIdentity();
  const somenteLeitura = identity?.perfil_leitura != null || !ativo;
  const [estado, setEstado] = useState<{ conversas: Conversa[]; carregando: boolean; erro: string | null }>({
    conversas: [],
    carregando: true,
    erro: null,
  });
  const preparado = useRef(false);

  const recarregar = useCallback(async () => {
    if (somenteLeitura) return setEstado({ conversas: [], carregando: false, erro: null });
    try {
      if (devePreparar && !preparado.current) {
        await preparar();
        preparado.current = true;
      }
      const conversas = ordenarConversas(await listarConversas());
      setEstado({ conversas, carregando: false, erro: null });
    } catch (e) {
      // No menu, sem mensagens (banco fora, migration pendente) o número só some.
      setEstado((s) => ({ ...s, carregando: false, erro: mensagemDeErro(e, 'Não foi possível carregar as conversas') }));
    }
  }, [somenteLeitura, devePreparar]);

  useEffect(() => {
    if (somenteLeitura) {
      setEstado({ conversas: [], carregando: false, erro: null });
      return;
    }
    let parar = () => {};
    let ativo = true;
    recarregar();
    supabase.auth.getSession().then(({ data }) => {
      const eu = data.session?.user.id;
      if (ativo && eu) parar = ouvirMensagens(eu, () => recarregar());
    });
    const t = window.setInterval(() => {
      if (naTela()) recarregar();
    }, INTERVALO_MS);
    window.addEventListener(EVENTO_MENSAGENS, recarregar);
    window.addEventListener('focus', recarregar);
    return () => {
      ativo = false;
      parar();
      window.clearInterval(t);
      window.removeEventListener(EVENTO_MENSAGENS, recarregar);
      window.removeEventListener('focus', recarregar);
    };
  }, [recarregar, somenteLeitura]);

  return { ...estado, total: totalNaoLidas(estado.conversas), somenteLeitura, recarregar };
}
